// Voici la page HomePage.jsx

import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ApiContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { IoPerson } from "react-icons/io5";
import { HiOfficeBuilding } from "react-icons/hi";
import { HiBriefcase } from "react-icons/hi";
import { HiCurrencyDollar } from "react-icons/hi";
import AddPartnerForm from "../components/partners/AddPartnerForm";
import Loading from '../components/utils/Loading';
import Breadcrumb from '../components/utils/Breadcrumb';
import NewPositionForm from '../components/position/NewPositionForm';
import CalendlyEventsCard from '../components/calendly/CalendlyEventsCard';


const UserApp = () => {
  const API = useContext(ApiContext);
  const { userData, loading: authLoading } = useAuth();
  const [partners, setPartners] = useState([]);
  const [positions, setPositions] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPositionForm, setShowPositionForm] = useState(false);
  const [prospectionProcesses, setProspectionProcesses] = useState([]);
  const [stats, setStats] = useState({
    totalPartners: 0,
    totalPositions: 0,
    totalProfiles: 0,
    totalCandidates: 0,
  });
  const [companyStats, setCompanyStats] = useState({
      totalRevenue: 0,
      totalBonus: 0,
      totalDeals: 0
    });

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Charger toutes les données essentielles en parallèle (sans /api/individuals qui est lourd)
        const [positionRes, statsRes, prospectionRes, dealsStats] = await Promise.all([
          API.get('/api/positions?limit=5'),
          API.get('/api/stats'),
          API.get('/api/process-bizdev?limit=5'),
          API.get('/api/deals/stats/company'),
        ]);

        setPositions(positionRes.data.slice(0, 5));
        setStats(statsRes.data);
        setProspectionProcesses(prospectionRes.data.slice(0, 5));
        setCompanyStats(dealsStats.data.stats.global);
        setLoading(false); // Afficher la page immédiatement

        // Charger les données lourdes en arrière-plan
        API.get('/api/individuals')
          .then(candidateRes => {
            setIndividuals(candidateRes.data);
          })
          .catch(error => {
            console.error('Error fetching individuals:', error);
          });
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // Afficher le loading pendant le chargement
  if (loading) {
    return (
      <div className="bg-gray-100  ">
        <Loading message="Chargement du tableau de bord..." fullHeight={true} />
      </div>
    );
  }


  return (
    <div className="p-4 sm:p-6 lg:p-10 mx-auto bg-gray-100 min-h-screen pt-16">
      
      {/* Breadcrumb */}
      <div className='pt-10'>
        <Breadcrumb items={[
        { label: 'Dashboard', href: null, icon: 'bi-speedometer2', isActive: true }
      ]} />
      </div>
      

      {/* Header avec salutation */}
      <div className="mb-6 sm:mb-8 p-4 sm:p-6 lg:p-8 bg-white border border-gray-100 rounded-2xl shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Photo utilisateur */}
            {authLoading ? (
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-200 rounded-full animate-pulse"></div>
            ) : userData?.profilePhotoUrl ? (
              <img
                src={userData.profilePhotoUrl}
                alt="Profil"
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center border-2 border-gray-200">
                <span className="text-white text-lg sm:text-xl font-bold">
                  {userData?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            )}

            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-light text-gray-700 m-0">
                Hello <span className='text-gray-900 font-medium'>{userData?.name || ''}</span>
              </h1>
              <p className="text-gray-500 text-xs sm:text-sm mt-1 mb-0">Welcome back to your dashboard</p>
            </div>
          </div>
          <div className="text-xl sm:text-2xl text-gray-300">👋</div>
        </div>
      </div>

      {/* Section Calendly + Stats en 2 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-6 sm:mb-8">
        {/* Colonne gauche : Calendly Events */}
        <div>
          <CalendlyEventsCard individuals={individuals} />
        </div>

        {/* Colonne droite : Stats en colonne */}
        <div className="flex flex-col gap-3">
          <Link
            to="/all-individuals"
            className="p-3 sm:p-4 bg-white border border-gray-200 transition hover:shadow-lg hover:scale-105 rounded-xl flex items-center no-underline cursor-pointer"
          >
            <div className='mr-3 lg:mr-4 text-lg sm:text-xl bg-blue-100 text-blue-600 p-2.5 sm:p-3 rounded-full'><IoPerson /></div>
            <div className="flex-1">
              <h2 className="text-xs sm:text-base lg:text-lg m-0 p-0 text-gray-800">Profils</h2>
              <p className="text-lg sm:text-xl lg:text-2xl m-0 p-0 font-semibold text-gray-900">
                {stats.totalIndividuals}
              </p>
              {stats.individualsThisMonth > 0 && (
                <span className="text-xs font-normal text-green-600">
                  {new Date().toLocaleDateString('fr-FR', { month: '2-digit', year: 'numeric' })} : + {stats.individualsThisMonth}
                </span>
              )}
            </div>
          </Link>

          <Link
            to="/partners"
            className="p-3 sm:p-4 bg-white border border-gray-200 transition hover:shadow-lg hover:scale-105 rounded-xl flex items-center no-underline cursor-pointer"
          >
            <div className='mr-3 lg:mr-4 text-lg sm:text-xl bg-emerald-100 text-emerald-600 p-2.5 sm:p-3 rounded-full'><HiOfficeBuilding /></div>
            <div className="flex-1">
              <h2 className="text-xs sm:text-base lg:text-lg m-0 p-0 text-gray-800">Clients</h2>
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold m-0 p-0 text-gray-900">{stats.totalPartners}</p>
            </div>
          </Link>

          <Link
            to="/process-rh"
            className="p-3 sm:p-4 bg-white border border-gray-200 transition hover:shadow-lg hover:scale-105 rounded-xl flex items-center no-underline cursor-pointer"
          >
            <div className='mr-3 lg:mr-4 text-lg sm:text-xl bg-purple-100 text-purple-600 p-2.5 sm:p-3 rounded-full'><HiBriefcase /></div>
            <div className="flex-1">
              <h2 className="text-xs sm:text-base lg:text-lg m-0 p-0 text-gray-800">Postes ouverts</h2>
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold m-0 p-0 text-gray-900">{stats.totalPositions}</p>
            </div>
          </Link>

          <Link
            to="/deals"
            className="p-3 sm:p-4 bg-white border border-gray-200 transition hover:shadow-lg hover:scale-105 rounded-xl flex items-center no-underline cursor-pointer"
          >
            <div className='mr-3 lg:mr-4 text-lg sm:text-xl bg-orange-100 text-orange-600 p-2.5 sm:p-3 rounded-full'><HiCurrencyDollar /></div>
            <div className="flex-1">
              <h2 className="text-xs sm:text-base lg:text-lg m-0 p-0 text-gray-800">Deals</h2>
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold m-0 p-0 text-gray-900">{companyStats.totalDeals}</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Partners Overview
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-2xl font-semibold m-0 p-0">Clients</h2>
        </div>
        <div className="grid grid-cols-3 gap-6">
          {partners.map((partner) => (
            <Link
                to={`/partners/${partner._id}`}
                className="no-underline"
              >
              <div key={partner._id} className="p-6 bg-white border border-gray-200 transition hover:shadow-lg hover:scale-[1.02] rounded-xl">
              <h3 className="text-lg font-bold text-gray-800 m-0">{partner.name}</h3>
              <p className="text-gray-600 mb-0">City: {partner.city}</p>
            </div>
             </Link>
          ))}
        </div>
      </div>
      */}

      {/* Section Postes Ouverts + Prospection en 2 colonnes */}
      <div className="mb-6 sm:mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Colonne gauche : Postes Ouverts */}
          {positions.length > 0 && (
            <div>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg sm:text-xl font-semibold m-0">Postes ouverts</h2>
              <Link to="/process-rh" className="text-xs text-blue-600 hover:text-blue-700 no-underline">
                Voir tout →
              </Link>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200 ">
              <table className="w-full table-auto bg-white">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-gray-700 text-sm">Intitulé du Poste</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-gray-700 text-sm">Entreprise</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-gray-700 text-sm">Ville</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.filter(position => position.open).slice(0, 5).map((position, index, array) => (
                    <tr key={position._id} className={`hover:bg-gray-50 ${index < array.length - 1 ? 'border-b border-gray-200' : ''}`}>
                      <td className="px-3 sm:px-4 py-2">
                        <Link
                          to={`/positions/${position._id}`}
                          className="text-gray-700 no-underline hover:text-indigo-700 font-medium text-sm"
                        >
                          {position.title}
                        </Link>
                      </td>
                      <td className="px-3 sm:px-4 py-2 text-sm">{position.partner?.name || '-'}</td>
                      <td className="px-3 sm:px-4 py-2 text-sm">{position.partner?.city || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
          
          {prospectionProcesses.length > 0 && (
            /* Colonne droite : Projets de Prospection */
            <div>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg sm:text-xl font-semibold m-0">Prospection en cours</h2>
              <Link to="/prospection" className="text-xs text-blue-600 hover:text-blue-700 no-underline">
                Voir tout →
              </Link>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200 ">
              <table className="w-full table-auto bg-white">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-gray-700 text-sm">Processus</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-gray-700 text-sm">Prospects</th>
                    <th className="px-3 sm:px-4 py-2 text-left text-gray-700 text-sm">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {prospectionProcesses.slice(0, 5).map((process, index, array) => (
                    <tr key={process._id} className={`hover:bg-gray-50 ${index < array.length - 1 ? 'border-b border-gray-200' : ''}`}>
                      <td className="px-3 sm:px-4 py-2">
                        <Link
                          to={`/process/bizdev/${process._id}`}
                          className="text-gray-700 no-underline hover:text-indigo-700 font-medium text-sm"
                        >
                          {process.name || 'N/A'}
                        </Link>
                      </td>
                      <td className="px-3 sm:px-4 py-2 text-sm">{process.prospects?.length || 0}</td>
                      <td className="px-3 sm:px-4 py-2 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${process.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {process.active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-lg sm:text-xl font-medium text-gray-800 mb-3 sm:mb-4">Actions rapides</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Bouton "Add Partner" */}
          <button
            onClick={() => setShowForm(!showForm)}
            className="group p-3 sm:p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md hover:border-blue-200 transition-all duration-200 flex flex-col items-center text-center"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-50 group-hover:bg-blue-100 rounded-xl flex items-center justify-center mb-2 transition-colors">
              {showForm ? (
                <i className="bi bi-x-lg text-blue-600 text-base sm:text-lg"></i>
              ) : (
                <i className="bi bi-building-add text-blue-600 text-base sm:text-lg"></i>
              )}
            </div>
            <span className="text-gray-700 font-medium text-xs sm:text-sm">
              {showForm ? 'Annuler' : 'Nouvelle Entreprise'}
            </span>
            <span className="text-gray-500 text-xs mt-0.5">
              {showForm ? 'Fermer le formulaire' : 'Ajouter une entreprise'}
            </span>
          </button>

          {/* Modal AddPartnerForm */}
          {showForm && (
            <AddPartnerForm
              onClose={() => setShowForm(false)}
            />
          )}

          {showPositionForm && (
            <NewPositionForm onClose={() => setShowPositionForm(false)} />
          )}

          <div
            onClick={() => setShowPositionForm(true)}
            className="cursor-pointer group p-3 sm:p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md hover:border-emerald-200 transition-all duration-200 flex flex-col items-center text-center no-underline"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-50 group-hover:bg-emerald-100 rounded-xl flex items-center justify-center mb-2 transition-colors">
              <i className="bi bi-briefcase-fill text-emerald-600 text-base sm:text-lg"></i>
            </div>
            <span className="text-gray-700 font-medium text-xs sm:text-sm">Nouveau Poste</span>
            <span className="text-gray-500 text-xs mt-0.5">Ajouter un poste</span>
          </div>

          <Link
            to="/analytics"
            className="group p-3 sm:p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md hover:border-purple-200 transition-all duration-200 flex flex-col items-center text-center no-underline"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-50 group-hover:bg-purple-100 rounded-xl flex items-center justify-center mb-2 transition-colors">
              <i className="bi bi-graph-up text-purple-600 text-base sm:text-lg"></i>
            </div>
            <span className="text-gray-700 font-medium text-xs sm:text-sm">Rapports</span>
            <span className="text-gray-500 text-xs mt-0.5">Voir les statistiques</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserApp;